import { FC } from "react";
import {
  IEntryRecord,
  IEntityMinimalModel,
  IGenericEntity,
  IModificationRecord,
  INamedEntity,
  ISoftDeletable,
  StringIndexedDict,
  ILockableEntity,
  IPermissionedEntity,
  ISignableEntity,
} from "../../api/GenericTypes";
import { GenericEntityConstantsEntities } from "../../api/GenericConstants";
import { ContentRowProps } from "../../common/tables/ContentRow/ContentRow";
import { GenericEntitySelectWidget, GenericEntitySelectWidgetProps } from "./common/GenericEntitySelectWdiget";
import { GenericEntityContentRow } from "./common/GenericEntityContentRow";
import { GenericEntityTile } from "./common/GenericEntityTile";
import { EntitySidebarTileProps } from "../../common/sidebar/tiles/EntitySidebarTile/EntitySidebarTile";
import { GenericEntityTable, GenericEntityTableProps } from "./common/GenericEntityTable";
import { GenericEntitySelectModal, GenericEntitySelectModalProps } from "./common/GenericEntitySelectModal";
import { GenericEntityDetailTable, GenericEntityDetailTableProps } from "./common/GenericEntityDetailTable";
import { GenericEntityPreviewModal, GenericEntityPreviewModalProps } from "./common/GenericEntityPreviewModal";
import { GenericEntityFilterForm } from "./common/GenericEntityFilterForm";
import { GenericEntityHistoryModal } from "./common/GenericEntityHistoryModal";
import { GenericEntityDetailPage, GenericEntityDetailPageProps } from "./common/GenericEntityDetailPage";

export type GenericEntity = IGenericEntity &
  INamedEntity &
  Partial<IEntryRecord> &
  Partial<IModificationRecord> &
  Partial<ISoftDeletable> &
  Partial<ILockableEntity> &
  Partial<ISignableEntity> &
  Partial<IPermissionedEntity>;

export interface GenericEntityComponents {
  ContentRow: FC<GenericEntityComponentProps<GenericEntity, ContentRowProps>>;
  DetailPage: FC<GenericEntityComponentProps<GenericEntity, GenericEntityDetailPageProps<GenericEntity>>>;
  DetailTable: FC<GenericEntityComponentProps<GenericEntity, GenericEntityDetailTableProps>>;
  FilterWidget: FC<GenericEntityComponentProps<GenericEntity, StringIndexedDict>>;
  PreviewModal: FC<GenericEntityComponentProps<GenericEntity, GenericEntityPreviewModalProps>>;
  SelectModal: FC<GenericEntityComponentProps<GenericEntity, GenericEntitySelectModalProps<GenericEntity>>>;
  SelectWidget: FC<
    GenericEntityComponentProps<
      GenericEntity,
      GenericEntitySelectWidgetProps<GenericEntity, IEntityMinimalModel, StringIndexedDict>
    >
  >;
  Table: FC<GenericEntityComponentProps<GenericEntity, GenericEntityTableProps<GenericEntity, StringIndexedDict>>>;
  Tile: FC<GenericEntityComponentProps<GenericEntity, EntitySidebarTileProps<GenericEntity, StringIndexedDict>>>;
}

export const DefaultGenericEntityComponents: GenericEntityComponents = Object.freeze({
  ContentRow: GenericEntityContentRow,
  DetailPage: GenericEntityDetailPage,
  DetailTable: GenericEntityDetailTable,
  FilterWidget: GenericEntityFilterForm,
  HistoryModal: GenericEntityHistoryModal,
  PreviewModal: GenericEntityPreviewModal,
  SelectModal: GenericEntitySelectModal,
  SelectWidget: GenericEntitySelectWidget,
  Table: GenericEntityTable,
  Tile: GenericEntityTile,
});

export type GenericEntityComponentKeysType = keyof GenericEntityComponents;

export const GenericEntityComponentKeys = Object.keys(
  DefaultGenericEntityComponents
) as GenericEntityComponentKeysType[];
export const IndexComponentKeys: GenericEntityComponentKeysType[] = [
  "FilterWidget",
  "SelectModal",
  "SelectWidget",
  "Table",
  "Tile",
];

export interface GenericEntityComponentProps<
  Entity extends GenericEntity = IEntityMinimalModel,
  ComponentProps extends StringIndexedDict = {}
> {
  entity?: Entity;
  entityTypeId: GenericEntityConstantsEntities;
  componentProps?: ComponentProps;
}
